import { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { FiTrash2 } from "react-icons/fi";

import { DeleteRowModal } from "./DeleteRowModal";
import { ZeroAllModal } from "./ZeroAllModal";
import { ZeroRowModal } from "./ZeroRowModal";
import { QuoteTableHeader } from "./QuoteTableHeader";
import { QuoteTableRow } from "./QuoteTableRow";
import { QuoteTableTotalsRow } from "./QuoteTableTotalsRow";
import { QuoteSummaryTotals } from "./QuoteSummaryTotals";
import type { SummaryTotalRow } from "./QuoteSummaryTotals";
import { cn } from "utils/helpers";
import { formatCurrency } from "./utils";
import type { QuoteTableColumn, QuoteTableMode, ColumnHeader } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import { SupplierRowsSection } from "../../Submission/components/QuoteTab/SupplierRowsSection";
import type { SupplierRowsSectionProps, SupplierRowsSectionHandle } from "../../Submission/components/QuoteTab/SupplierRowsSection";

export interface TotalRow {
	id: string;
	name: string;
	color: string;
	value: number;
}

export interface UnitOption {
	label: string;
	value: number;
}

/**
 * Parsed row data from a clipboard paste (tab/newline matrix).
 * Each entry maps column IDs to pasted cell values.
 */
export interface PastedRowData {
	cells: Record<number, string>;
	unitId?: number | null;
}

export interface DynamicQuoteTableProps {
	allValues?: Record<string, Record<string, number | string>>;
	columns: QuoteTableColumn[];
	columnValues?: Record<number, Record<number, string>>;
	dirtyRowValues?: Record<number, { unit_id?: number | null; name?: string; }>;
	disabled: boolean;
	editValues?: Record<number, Record<number, { quantity: string; rate: string }>>;
	includeTotalsRow?: boolean;
	items: RfxQuoteItem[];
	mode?: QuoteTableMode;
	rowErrors?: Record<number, { unit_id?: boolean; cells?: Record<number, boolean>; name?: boolean; }>;
	supplierColumnTotals?: Record<number, number>;
	supplierRowsProps?: Omit<SupplierRowsSectionProps, 'disabled' | 'isClientView'>;
	supplierRowsRef?: React.Ref<SupplierRowsSectionHandle>;
	totals?: TotalRow[];
	unitOptions?: UnitOption[];
	/**
	 * Called when a multi-cell paste is detected on a description cell.
	 * Receives the current row (to update in-place) plus any additional
	 * rows that need to be created.
	 */
	onBulkDeleteRows?: (rowIds: number[]) => void;
	onCellValueChange?: (itemId: number, columnId: number, field: string, value: string) => void;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onPasteRows?: (currentItemId: number, currentRowCells: Record<number, string>, newRows: PastedRowData[]) => void;
	onRowDelete?: (rowId: number) => void;
	onRowReorder?: (rowIds: number[]) => void;
	onRowUpdate?: (rowId: number, payload: { unit_id?: number | null; name?: string; }) => void;
}

const DynamicQuoteTable: React.FC<DynamicQuoteTableProps> = ({
	allValues = {},
	columns,
	disabled,
	items,
	mode,
	onBulkDeleteRows,
	onPasteRows,
	onRowDelete,
	onRowReorder,
	onRowUpdate,
	unitOptions = [],
	dirtyRowValues = {},
	rowErrors = {},
	editValues = {},
	columnValues = {},
	includeTotalsRow = false,
	onCellValueChange,
	onFormikFieldChange,
	supplierColumnTotals = {},
	supplierRowsProps,
	supplierRowsRef,
	totals = [],
}) => {
	const isProcurerView = mode === "edit";
	const isClientView = mode === "view";
	const showActionsColumn = isProcurerView && !disabled && !!onRowDelete;
	const showSelectionColumn = isProcurerView && !disabled && !!onBulkDeleteRows;

	// --- Row selection state ---
	const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());

	// Clear selection when items change (e.g. after delete)
	useEffect(() => {
		setSelectedRowIds(new Set());
	}, [items]);

	const toggleRowSelection = useCallback((rowId: number) => {
		setSelectedRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(rowId)) {
				next.delete(rowId);
			} else {
				next.add(rowId);
			}
			return next;
		});
	}, []);

	const allRowsSelected = items.length > 0 && items.every((i) => selectedRowIds.has(i.id));
	const someRowsSelected = selectedRowIds.size > 0 && !allRowsSelected;

	const toggleSelectAllRows = useCallback(() => {
		if (allRowsSelected) {
			setSelectedRowIds(new Set());
		} else {
			setSelectedRowIds(new Set(items.map((i) => i.id)));
		}
	}, [allRowsSelected, items]);

	const [pendingBulkDelete, setPendingBulkDelete] = useState(false);

	const handleConfirmBulkDelete = useCallback(() => {
		if (onBulkDeleteRows && selectedRowIds.size > 0) {
			onBulkDeleteRows(Array.from(selectedRowIds));
		}
		setPendingBulkDelete(false);
		setSelectedRowIds(new Set());
	}, [onBulkDeleteRows, selectedRowIds]);

	const duplicateColumnNames = useMemo(() => {
		const labelCounts = new Map<string, number>();
		for (const col of columns) {
			const lower = col.label.toLowerCase();
			labelCounts.set(lower, (labelCounts.get(lower) || 0) + 1);
		}
		const dupes = new Set<string>();
		for (const [label, count] of labelCounts) {
			if (count > 1) {
				dupes.add(label);
			}
		}
		return dupes;
	}, [columns]);

	const isRespondMode = mode === "respond";
	const showZeroButton = isRespondMode && !disabled;

	const headers = useMemo((): ColumnHeader[] => {
		const result: ColumnHeader[] = [
			{ key: "idx", label: "#", width: showSelectionColumn ? "5%" : "3%" },
		];
		const sortedCols = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
		for (const col of sortedCols) {
			result.push({
				key: `col-${col.id}`,
				label: col.label,
				width: col.columnType === "description" ? "25%" : "12%",
				columnId: col.id,
				columnType: col.columnType,
				isDefault: col.isDefault,
				isFirstOfGroup: true,
			});
		}
		return result;
	}, [columns, showSelectionColumn]);

	// Summary totals — convert TotalRow (numeric) to SummaryTotalRow (pre-formatted)
	const { summaryTotals, summarySpacerColSpan, summaryTrailingColSpan } = useMemo(() => {
		const hasTotalCol = columns.some((c) => c.columnType === "total");
		if (!hasTotalCol || totals.length === 0) {
			return { summaryTotals: [] as SummaryTotalRow[], summarySpacerColSpan: 1, summaryTrailingColSpan: 0 };
		}
		const costIdx = headers.findIndex((h) => h.columnType === "total");
		const costColIndex = costIdx >= 0 ? costIdx : headers.length - 1;
		const labelColIndex = costColIndex - 1;
		const spacer = Math.max(labelColIndex, 1) + (showZeroButton ? 1 : 0);
		const trailing = headers.length - costColIndex - 1 + (showActionsColumn ? 1 : 0);

		const mapped: SummaryTotalRow[] = totals.map((row) => ({
			id: row.id,
			label: row.name,
			color: row.color,
			displayValue: formatCurrency(row.value),
		}));

		return { summaryTotals: mapped, summarySpacerColSpan: spacer, summaryTrailingColSpan: trailing };
	}, [columns, headers, totals, showZeroButton, showActionsColumn]);

	// Columns that get zeroed
	const numericColIds = useMemo(() => {
		return columns
			.filter((c) =>
				c.columnType === "response_value" ||
				c.columnType === "saving" ||
				c.columnType === "response_quantity" ||
				c.columnType === "response_number"
			)
			.map((c) => ({ id: c.id, type: c.columnType }));
	}, [columns]);

	// --- Row management ---
	const [pendingDeleteRowId, setPendingDeleteRowId] = useState<number | null>(null);
	const [pendingZeroRowId, setPendingZeroRowId] = useState<number | null>(null);

	const handleConfirmDeleteRow = useCallback(() => {
		if (pendingDeleteRowId && onRowDelete) {
			onRowDelete(pendingDeleteRowId);
		}
		setPendingDeleteRowId(null);
	}, [pendingDeleteRowId, onRowDelete]);

	const zeroRow = useCallback((rowId: number) => {
		if (!onFormikFieldChange) {
			return;
		}
		for (const col of numericColIds) {
			if (col.type === "response_quantity") {
				onFormikFieldChange(rowId, "quantity", 0);
			} else if (col.type === "response_value" || col.type === "saving") {
				onFormikFieldChange(rowId, `rate_${col.id}`, 0);
			} else if (col.type === "response_number") {
				onFormikFieldChange(rowId, `number_${col.id}`, 0);
			}
		}
	}, [numericColIds, onFormikFieldChange]);

	const handleZeroClick = useCallback((rowId: number) => {
		// Check if row has any existing values
		const formikValues = allValues[`quote-${rowId}`];
		const hasValues = numericColIds.some((col) => {
			let val: number | string | undefined;
			if (col.type === "response_quantity") {
				val = formikValues?.["quantity"];
			} else if (col.type === "response_value" || col.type === "saving") {
				val = formikValues?.[`rate_${col.id}`];
			} else if (col.type === "response_number") {
				val = formikValues?.[`number_${col.id}`];
			}
			return val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0";
		});

		if (hasValues) {
			setPendingZeroRowId(rowId);
		} else {
			zeroRow(rowId);
		}
	}, [allValues, numericColIds, zeroRow]);

	const handleConfirmZeroRow = useCallback(() => {
		if (pendingZeroRowId !== null) {
			zeroRow(pendingZeroRowId);
		}
		setPendingZeroRowId(null);
	}, [pendingZeroRowId, zeroRow]);

	// --- Zero All logic ---
	const [pendingZeroAll, setPendingZeroAll] = useState(false);

	const zeroAllRows = useCallback((onlyEmpty: boolean) => {
		if (!onFormikFieldChange) {
			return;
		}
		for (const item of items) {
			const formikValues = allValues[`quote-${item.id}`];
			for (const col of numericColIds) {
				let field: string;
				if (col.type === "response_quantity") {
					field = "quantity";
				} else if (col.type === "response_value" || col.type === "saving") {
					field = `rate_${col.id}`;
				} else if (col.type === "response_number") {
					field = `number_${col.id}`;
				} else {
					continue;
				}
				if (onlyEmpty) {
					const val = formikValues?.[field];
					if (val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0") {
						continue;
					}
				}
				onFormikFieldChange(item.id, field, 0);
			}
		}
	}, [allValues, items, numericColIds, onFormikFieldChange]);

	const handleZeroAllClick = useCallback(() => {
		// Check if any row has any existing values
		const hasAnyValues = items.some((item) => {
			const formikValues = allValues[`quote-${item.id}`];
			return numericColIds.some((col) => {
				let val: number | string | undefined;
				if (col.type === "response_quantity") {
					val = formikValues?.["quantity"];
				} else if (col.type === "response_value" || col.type === "saving") {
					val = formikValues?.[`rate_${col.id}`];
				} else if (col.type === "response_number") {
					val = formikValues?.[`number_${col.id}`];
				}
				return val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0";
			});
		});

		if (hasAnyValues) {
			setPendingZeroAll(true);
		} else {
			zeroAllRows(false);
		}
	}, [allValues, items, numericColIds, zeroAllRows]);

	const handleZeroAllConfirm = useCallback(() => {
		zeroAllRows(false);
		setPendingZeroAll(false);
	}, [zeroAllRows]);

	const handleZeroEmptyConfirm = useCallback(() => {
		zeroAllRows(true);
		setPendingZeroAll(false);
	}, [zeroAllRows]);

	const handleRowDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination || !onRowReorder) {
				return;
			}
			const startIndex = result.source.index;
			const endIndex = result.destination.index;
			if (startIndex === endIndex) {
				return;
			}
			const ids = items.map((i) => i.id);
			const [movedId] = ids.splice(startIndex, 1);
			ids.splice(endIndex, 0, movedId);
			onRowReorder(ids);
		},
		[items, onRowReorder]
	);

	// --- Auto-calculated column totals ---
	const columnTotals = useMemo(() => {
		if (!includeTotalsRow) {
			return null;
		}
		const totalsMap: Record<number, number> = {};
		const responseValueCols = columns.filter(
			(c) => c.columnType === "response_value" || c.columnType === "saving"
		);
		for (const col of responseValueCols) {
			let sum = 0;
			for (const item of items) {
				const formikVal = allValues[`quote-${item.id}`]?.[`rate_${col.id}`];
				const val = Number(formikVal ?? columnValues[item.id]?.[col.id] ?? editValues[item.id]?.[col.id]?.rate ?? 0);
				if (!isNaN(val)) {
					sum += val;
				}
			}
			totalsMap[col.id] = sum + (supplierColumnTotals[col.id] ?? 0);
		}
		return totalsMap;
	}, [includeTotalsRow, columns, items, allValues, columnValues, editValues, supplierColumnTotals]);

	// --- Cell props shared across all rows ---
	const cellProps = useMemo(() => ({
		allValues,
		columns,
		columnValues,
		disabled,
		dirtyRowValues,
		editValues,
		mode: mode ?? ("respond" as QuoteTableMode),
		onCellValueChange,
		onFormikFieldChange,
		onPasteRows,
		onRowUpdate,
		rowErrors,
		unitOptions,
	}), [allValues, columns, columnValues, disabled, dirtyRowValues, editValues, mode, onCellValueChange, onFormikFieldChange, onPasteRows, onRowUpdate, rowErrors, unitOptions]);

	return (
		<>
			{showSelectionColumn && selectedRowIds.size > 0 && (
				<div
					className="mb-2 flex items-center gap-3 rounded bg-blue-600 px-4 py-2 text-white shadow"
					data-testid="row-bulk-actions-bar"
				>
					<span className="text-sm font-semibold">
						{selectedRowIds.size} {selectedRowIds.size === 1 ? "row" : "rows"} selected
					</span>
					<div className="flex-1" />
					<button
						type="button"
						className="flex items-center gap-1 rounded bg-red-500 px-3 py-1 text-sm font-medium text-white hover:bg-red-600"
						onClick={() => setPendingBulkDelete(true)}
						data-testid="bulk-delete-rows-btn"
					>
						<FiTrash2 size={14} />
						Delete Selected
					</button>
				</div>
			)}

			{showZeroButton && (
				<div className="mb-2 flex justify-start">
					<button
						type="button"
						className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800"
						onClick={handleZeroAllClick}
						data-testid="zero-all-btn"
					>
						Zero All
					</button>
				</div>
			)}

			<div
				className="overflow-x-auto"
				data-testid="dynamic-quote-table-wrapper"
			>
				<table
					className="w-full table-fixed rounded-xl border-b border-solid border-[#EAECF0] bg-white"
					style={{ minWidth: `${Math.max(800, headers.length * 100)}px` }}
					data-testid="dynamic-quote-table"
				>
					<caption className="sr-only">Quote table</caption>

					<QuoteTableHeader
						columns={columns}
						duplicateColumnNames={duplicateColumnNames}
						headers={headers}
						showActionsColumn={showActionsColumn}
						showSelectionColumn={showSelectionColumn}
						isAllSelected={allRowsSelected}
						isSomeSelected={someRowsSelected}
						onToggleSelectAll={toggleSelectAllRows}
						showZeroColumn={showZeroButton}
					/>


					<DragDropContext onDragEnd={handleRowDragEnd}>
						<Droppable droppableId="quote-table-rows" direction="vertical">
							{(droppableProvided) => (
								<tbody
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
								>
									{items.map((item, idx) => (
										<Draggable
											key={item.id}
											draggableId={`row-${item.id}`}
											index={idx}
											isDragDisabled={!isProcurerView || disabled || !onRowReorder}
										>
											{(draggableProvided) => (
												<tr
													ref={draggableProvided.innerRef}
													{...draggableProvided.draggableProps}
													className={cn(
														"group border-b border-[#EAECF0]",
														selectedRowIds.has(item.id) && "bg-blue-50"
													)}
												>
													<QuoteTableRow
														cellProps={cellProps}
														columns={columns}
														dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
														headers={headers}
														index={idx}
														isSelected={selectedRowIds.has(item.id)}
														item={item}
														onDeleteClick={setPendingDeleteRowId}
														onToggleSelect={showSelectionColumn ? toggleRowSelection : undefined}
														onZeroClick={handleZeroClick}
														showDeleteAction={showActionsColumn}
														showDragHandle={isProcurerView && !disabled && !!onRowReorder}
														showZeroButton={showZeroButton}
													/>
												</tr>
											)}
										</Draggable>
									))}
									{droppableProvided.placeholder}
								</tbody>
							)}
						</Droppable>
					</DragDropContext>

					{supplierRowsProps && (
						<SupplierRowsSection
							ref={supplierRowsRef}
							disabled={disabled}
							isClientView={isClientView}
							showZeroColumn={showZeroButton}
							{...supplierRowsProps}
						/>
					)}

					{columnTotals && (
						<QuoteTableTotalsRow
							columns={columns}
							columnTotals={columnTotals}
							showActionsColumn={showActionsColumn}
							showZeroColumn={showZeroButton}
						/>
					)}

					{summaryTotals.length > 0 && (
						<tbody>
							<QuoteSummaryTotals
								spacerColSpan={summarySpacerColSpan}
								totals={summaryTotals}
								trailingColSpan={summaryTrailingColSpan}
							/>
						</tbody>
					)}
				</table>
			</div>

			<DeleteRowModal
				isOpen={pendingDeleteRowId !== null}
				onClose={() => setPendingDeleteRowId(null)}
				onConfirm={handleConfirmDeleteRow}
			/>

			<ZeroRowModal
				isOpen={pendingZeroRowId !== null}
				onClose={() => setPendingZeroRowId(null)}
				onConfirm={handleConfirmZeroRow}
			/>

			<ZeroAllModal
				isOpen={pendingZeroAll}
				onClose={() => setPendingZeroAll(false)}
				onZeroAll={handleZeroAllConfirm}
				onZeroEmpty={handleZeroEmptyConfirm}
			/>

			<DeleteRowModal
				isOpen={pendingBulkDelete}
				onClose={() => setPendingBulkDelete(false)}
				onConfirm={handleConfirmBulkDelete}
				message={`Are you sure you want to delete ${selectedRowIds.size} ${selectedRowIds.size === 1 ? "row" : "rows"}? This action cannot be undone.`}
			/>
		</>
	);
};

export { DynamicQuoteTable };
